import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState
} from 'react';
import { useSelector } from 'react-redux';

import { ContainedButton } from '../../components/buttons';
import { SHOPIFY_PRODUCT_TAG } from '../../config';
import { findProductVariant } from '../../utils';
import { AppStates } from '../../types';
import { useRootSelector } from '../../store';

import { selectChosenUpsellItems } from '../upsells/store/upsellsSlice';
import {
  selectCartItem,
  selectItemProperties,
  selectSlots
} from '../item/store/itemSlice';
import { useFontsAndFields } from '../fonts/TextAndFont.hooks';
import { useIconsAndLabels } from '../iconAndLabel/IconAndLabelHooks';
import { useMaterials } from '../clothingType/Materials.hooks';
import { useGetProductQuery } from '../api';
import { useLabelShape } from '../LabelShapes/LabelShapeHooks';
import { CartItemState } from '../app/Configurator';
import {
  selectAllConfigSnapshots,
  selectIsEditingMultiConfigSession,
  selectMultiConfigEnabled,
  useValidateMultiConfigNames
} from '../multiConfig';

import {
  useSaveMultiConfigSessionEdit,
  useSendMultiConfigSessionToCart
} from './useAddToCart';
import {
  addShopifyCartItem,
  addShopifyCartItems,
  buildPrimaryApiCartItem,
  buildUpsellApiCartItem,
  getQuantityProperty,
  removeShopifyCartItem,
  removeShopifyCartItems,
  sendApiCartItem,
  updateApiCartItem,
  updateShopifyCartItem
} from './utils';

const { NEW_ADD_TO_CART } = AppStates;

declare global {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, no-var
  var Shopify: any;
}

type ShopifyCartItemUpdate = {
  id: number;
  quantity: number;
  properties: Record<string, string>;
};

function base64UrlEncodeJson(obj: unknown): string {
  const json = JSON.stringify(obj ?? {});

  // Browser environment
  const utf8Bytes = new TextEncoder().encode(json);
  let binary = '';
  for (const b of utf8Bytes) binary += String.fromCharCode(b);

  const b64 = btoa(binary);
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function toShopifyCartPermalink(
  input: {
    items: Array<{
      id: string;
      quantity: number;
      properties?: Record<string, unknown>;
    }>;
  },
  shopDomain = 'namebubbles.com'
): string {
  if (!input?.items?.length) throw new Error('No items provided');

  const itemsPart = input.items
    .map(
      it =>
        `${encodeURIComponent(String(it.id))}:${encodeURIComponent(
          String(it.quantity)
        )}`
    )
    .join(',');

  const base = `https://${shopDomain}/cart/${itemsPart}`;

  const firstProps = input.items[0]?.properties;
  if (!firstProps || Object.keys(firstProps).length === 0) return base;

  const encoded = base64UrlEncodeJson(firstProps);
  return `${base}?properties=${encodeURIComponent(encoded)}`;
}

interface AddToCartButtonProps {
  productTag?: string;
  embedShopifyVariantId?: string;
  style?: React.CSSProperties;
  cartItemId?: string;
  isReadyForCart: boolean;
  // Unlike the general isReadyForCart gate (which also covers ordinary
  // validation errors, and deliberately leaves the button clickable so a
  // click surfaces the specific error toast), this visually disables the
  // button - the shopper has nothing left to configure, just more
  // configurations to add, so there's no error to click into.
  belowConfigMinimum?: boolean;
  setAppState: Dispatch<SetStateAction<AppStates>>;
  onClick?: () => void;
  preSelectedUpsells?: CartItemState[];
  isEmbedMode?: boolean;
  customButtonText?: string;
  onComplete?: () => void;
  onEmbedAddToCart?: (item: {
    variantId: number;
    quantity: number;
    properties: Record<string, string>;
  }) => void;
}

export function AddToCartButton({
  productTag,
  embedShopifyVariantId,
  style,
  cartItemId,
  isReadyForCart,
  belowConfigMinimum = false,
  setAppState,
  onClick,
  preSelectedUpsells = [],
  isEmbedMode = false,
  customButtonText,
  onComplete,
  onEmbedAddToCart
}: AddToCartButtonProps) {
  const params = new URLSearchParams(window.location.search);
  const legacyItemKey = params.get('legacy_item_key') || undefined;
  const legacyUpsellKeys = params.getAll('upsell');

  const updatingCartItem = !!(cartItemId || legacyItemKey);

  const properties = useSelector(selectItemProperties);
  const primaryCartItemProperties = useSelector(selectCartItem);
  const chosenUpsellItems = useSelector(selectChosenUpsellItems);
  const defaultButtonText = updatingCartItem ? 'Save' : 'Add to Cart';
  const [buttonText, setButtonText] = useState(
    customButtonText || defaultButtonText
  );
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [buttonError, setButtonError] = useState<string | null>(null);

  useEffect(() => {
    setButtonText(customButtonText || defaultButtonText);
  }, [updatingCartItem, isEmbedMode, customButtonText, defaultButtonText]);

  const { fonts, hideFontsAndFields } = useFontsAndFields();
  const { showIcons, labelHasMultipleVariants } = useIconsAndLabels();
  const { showLabelShapes } = useLabelShape();
  const { materials } = useMaterials();
  const { data: apiProduct } = useGetProductQuery(
    productTag || SHOPIFY_PRODUCT_TAG
  );
  const slots = useRootSelector(selectSlots);

  const multiConfigEnabled = useRootSelector(selectMultiConfigEnabled);
  const isEditingMultiConfigSession = useRootSelector(
    selectIsEditingMultiConfigSession
  );
  const configSnapshots = useRootSelector(selectAllConfigSnapshots);
  const sendMultiConfigSession = useSendMultiConfigSessionToCart(productTag);
  const saveMultiConfigSessionEdit = useSaveMultiConfigSessionEdit(productTag);
  const validateMultiConfigNames = useValidateMultiConfigNames();

  const sendMultiConfigSessionToCart = useCallback(async () => {
    const result = isEditingMultiConfigSession
      ? await saveMultiConfigSessionEdit()
      : await sendMultiConfigSession();

    if (!result.success) {
      setButtonText(result.error || 'Error adding to cart');
      setButtonDisabled(true);
      return;
    }

    if (onComplete) {
      onComplete();
    } else {
      setAppState(NEW_ADD_TO_CART);
    }
  }, [
    isEditingMultiConfigSession,
    saveMultiConfigSessionEdit,
    sendMultiConfigSession,
    onComplete,
    setAppState
  ]);

  const sendItemToCart = useCallback(async () => {
    if (
      multiConfigEnabled &&
      configSnapshots.length > 0 &&
      (!cartItemId || isEditingMultiConfigSession)
    ) {
      return sendMultiConfigSessionToCart();
    }

    const primaryApiCartItem = buildPrimaryApiCartItem(properties, apiProduct);

    let primaryCartItemId;
    try {
      if (cartItemId) {
        primaryCartItemId = await updateApiCartItem(
          cartItemId,
          primaryApiCartItem
        );
      } else {
        primaryCartItemId = await sendApiCartItem(primaryApiCartItem);
      }
    } catch (error) {
      setButtonText('Error adding to cart');
      setButtonDisabled(true);
      return;
    }

    const cartItemIdPromises = [];
    const upsellShopifyCartItems: ShopifyCartItemUpdate[] = [];
    const upsellUpdateProperties = new Map<string, Record<string, string>>();

    for (const chosenUpsell of chosenUpsellItems) {
      const { upsell, material, cartItemId: upsellCartItemId } = chosenUpsell;

      const upsellProperties: { [key: string]: string } = {};

      const labelQuantity = upsell.label_slots.reduce((acc, slot) => {
        const { slot_options } = slot;
        const [option] = slot_options;
        return (acc += option?.quantity ?? 0);
      }, 0);

      upsellProperties.Labels = labelQuantity.toString();

      const { fields } = properties;
      const configuredTextFields = Object.values(fields);

      // Name or Full Name must be added first
      const prioritizedDisplayNames = ['Name', 'Full Name'];
      const prioritizedTextField = configuredTextFields.find(field =>
        prioritizedDisplayNames.includes(field.display_name!)
      );

      if (prioritizedTextField) {
        const { display_name, value } = prioritizedTextField;
        upsellProperties[display_name!] = value;
      }

      for (const { display_name, value } of configuredTextFields) {
        const isAlreadyAdded =
          prioritizedTextField &&
          display_name === prioritizedTextField.display_name;

        if (isAlreadyAdded) continue;

        upsellProperties[display_name!] = value;
      }

      const { style, palette, font, icon } = properties;

      upsellProperties.Style = style!.display_name;
      upsellProperties.Color = palette!.display_name;

      if (fonts?.length > 1) {
        upsellProperties.Font = font!.display_name;
      }

      if (showIcons) {
        upsellProperties.Icon = icon!.display_name;
      }

      upsellProperties['Clothing Type'] = material?.display_name;

      upsellProperties._order_system = 'NEW';
      upsellProperties._parent_item_id = primaryCartItemId;

      if (properties.style?.style_id)
        upsellProperties._style_id = properties.style?.style_id;
      if (properties.palette?.palette_id)
        upsellProperties._palette_id = properties.palette?.palette_id;
      if (properties.font?.font_id)
        upsellProperties._font_id = properties.font?.font_id;
      if (properties.icon?.icon_id)
        upsellProperties._icon_id = properties.icon?.icon_id;
      if (material?.material_id)
        upsellProperties._material_id = material?.material_id;

      const upsellApiCartItemData = buildUpsellApiCartItem(
        chosenUpsell,
        properties,
        primaryApiCartItem
      );

      const fetchAndMountCartItemId = async () => {
        if (upsellCartItemId) {
          await updateApiCartItem(upsellCartItemId, upsellApiCartItemData);
          upsellProperties._cart_item_id = upsellCartItemId;
        } else {
          const upsellCartItemId = await sendApiCartItem(upsellApiCartItemData);
          upsellProperties._cart_item_id = upsellCartItemId;
        }
      };

      if (!upsellCartItemId) {
        upsellShopifyCartItems.push({
          id: upsell.shopify_id!,
          quantity: 1,
          properties: upsellProperties
        });
      } else {
        upsellUpdateProperties.set(upsellCartItemId, upsellProperties);
      }

      cartItemIdPromises.push(fetchAndMountCartItemId());
    }

    // the order of how the following properties are added controls
    // the order in which they are displayed in the cart

    // arrange cart properties for display
    const {
      Font,
      ['Clothing Type']: clothingType,
      Icon,
      Style,
      Palette,
      ...otherPrimaryCartItemProperties
    } = primaryCartItemProperties;

    // next show the Field Names. "Name" or "Full Name" should always be first.
    const fieldNames: { [key: string]: string | undefined } = {};

    const hasNameField = otherPrimaryCartItemProperties.Name;
    if (hasNameField) {
      fieldNames.Name = otherPrimaryCartItemProperties.Name;
      delete otherPrimaryCartItemProperties.Name;
    }

    const hasFullNameField = otherPrimaryCartItemProperties['Full Name'];
    if (hasFullNameField) {
      fieldNames['Full Name'] = otherPrimaryCartItemProperties['Full Name'];
      delete otherPrimaryCartItemProperties['Full Name'];
    }

    if (!hideFontsAndFields) {
      Object.entries(otherPrimaryCartItemProperties).forEach(([key, value]) => {
        if (!key.startsWith('_')) {
          fieldNames[key] = value;
          delete otherPrimaryCartItemProperties[key];
        }
      });
    }

    const userProperties: { [key: string]: string | undefined } = {
      Style,
      Color: Palette
    };

    // Show the selected font if there were multiple fonts to choose from
    if (fonts?.length > 1) {
      userProperties.Font = Font;
    }

    // Show the selected icon if the icon selection step is shown
    if (showIcons) {
      userProperties.Icon = Icon;
    }

    // If there is a LabelShape _choice_, show that;
    // For each slot:
    //   If there is a label variant _choice_, show that:
    const shapes = [];
    if (showLabelShapes && properties.labelShape?.display_name) {
      shapes.push(properties.labelShape.display_name);
    }
    apiProduct?.label_slots?.map(labelSlot => {
      const labelId = slots?.[labelSlot.product_label_slot_id]?.labelId;
      const label = apiProduct.labels.find(l => l.label_id === labelId);
      const isIterative = label?.iterative_style_ids?.includes(
        properties?.style?.style_id ?? ''
      );
      const hasMultipleVariants = labelHasMultipleVariants[labelId || ''];

      if (hasMultipleVariants && !isIterative) {
        // Add selected variant
        const variantId =
          properties.slots[labelSlot.product_label_slot_id].variantId;
        const variant = label?.variants.find(
          v => v.label_variant_id === variantId
        );
        if (variant?.system_name) shapes.push(variant.system_name);
      }
    });

    if (shapes.length) {
      userProperties.Shape = shapes.join(', ');
    }

    // Show the selected material if there were multiple materials to choose from
    if (materials?.length > 1) {
      userProperties['Clothing Type'] = clothingType;
    }

    // get the number of labels.
    const quantity = getQuantityProperty(properties.slots, apiProduct);

    const formattedItemProperties = {
      Labels: quantity.toString(),
      ...fieldNames,
      ...userProperties,
      ...otherPrimaryCartItemProperties,
      _order_system: 'NEW',
      _cart_item_id: primaryCartItemId,
      _product_id: apiProduct?.product_id,
      _style_id: properties.style?.style_id,
      _palette_id: properties.palette?.palette_id,
      _font_id: properties.font?.font_id,
      _icon_id: properties.icon?.icon_id,
      _material_id: properties.clothingType?.material_id
    };

    const { labelShape, palette, style } = properties;
    const productVariant = findProductVariant(labelShape, palette, style);

    const primaryProductItem = {
      id: productVariant?.id,
      quantity: 1,
      properties: formattedItemProperties
    };

    const productItems = cartItemId ? [] : [primaryProductItem];
    try {
      await Promise.all(cartItemIdPromises);
    } catch (error) {
      setButtonText('Error adding to cart');
      setButtonDisabled(true);
      return;
    }
    if (cartItemId) {
      const params = new URLSearchParams(window.location.search);
      const key = params.get('key')!;
      const [variantId, _hash] = key.split(':');

      // if the key in the URL does not match the variant id, remove the cart item
      // and re-add the correct one
      if (Number(variantId) !== primaryProductItem.id) {
        await removeShopifyCartItem(key);
        await addShopifyCartItem(primaryProductItem);
      } else {
        await updateShopifyCartItem(key, primaryProductItem);
      }
    }

    const items = [...productItems, ...upsellShopifyCartItems];

    if (items.length) {
      const response = await addShopifyCartItems(items);

      if (legacyItemKey) {
        const legacyKeysToRemove = [legacyItemKey, ...legacyUpsellKeys];
        await removeShopifyCartItems(legacyKeysToRemove);
      }

      if (!response.ok) {
        const json = await response.json();
        console.error('error adding to cart', json);
        setButtonText('Error adding to cart');
        setButtonDisabled(true);
        return;
      }
    }

    if (preSelectedUpsells?.length) {
      for (const upsell of preSelectedUpsells) {
        if (upsell.cart_item_id) {
          const properties = upsellUpdateProperties.get(upsell.cart_item_id);
          if (properties) {
            await fetch('/cart/change.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                id: upsell.shopify_key,
                properties: properties
              })
            });
          } else {
            await removeShopifyCartItem(upsell.shopify_key!);
          }
        }
      }
    }

    if (onComplete) {
      onComplete();
    } else {
      setAppState(NEW_ADD_TO_CART);
    }
  }, [
    properties,
    apiProduct,
    primaryCartItemProperties,
    hideFontsAndFields,
    fonts?.length,
    showIcons,
    showLabelShapes,
    materials?.length,
    cartItemId,
    preSelectedUpsells,
    setAppState,
    onComplete,
    chosenUpsellItems,
    slots,
    labelHasMultipleVariants,
    legacyItemKey,
    legacyUpsellKeys,
    multiConfigEnabled,
    isEditingMultiConfigSession,
    configSnapshots,
    sendMultiConfigSessionToCart
  ]);

  // TODO: Move this to a helper function
  const redirectToShopifyCheckout = useCallback(async () => {
    // Get URL parameters
    const params = new URLSearchParams(window.location.search);
    const partnerKey = params.get('partner_key') || '';
    const shopifyVariantId =
      embedShopifyVariantId || params.get('shopify_variant_id');
    const originalDom = params.get('shopify_domain');
    const shopifyDomain =
      import.meta.env.VITE_SHOPIFY_DOMAIN || originalDom || '';
    if (!shopifyVariantId) {
      setButtonText(customButtonText || defaultButtonText);
      setButtonDisabled(false);
      setButtonError(
        'Configuration error: missing variant ID. Try refreshing the page.'
      );
      console.error(
        'shopify_variant_id parameter is required for embed checkout'
      );
      return;
    }

    // Step 1: Create cart item in our API to save the configuration
    const primaryApiCartItem = buildPrimaryApiCartItem(properties, apiProduct);

    let primaryCartItemId;
    try {
      primaryCartItemId = await sendApiCartItem(primaryApiCartItem);
    } catch (error) {
      console.error('Error creating cart item:', error);
      setButtonText('Error creating cart item');
      setButtonDisabled(true);
      return;
    }

    // Step 2: Build cart item properties for Shopify
    const {
      Font,
      ['Clothing Type']: clothingType,
      Icon,
      Style,
      Palette,
      ...otherPrimaryCartItemProperties
    } = primaryCartItemProperties;

    const fieldNames: { [key: string]: string | undefined } = {};
    const hasNameField = otherPrimaryCartItemProperties.Name;
    if (hasNameField) {
      fieldNames.Name = otherPrimaryCartItemProperties.Name;
      delete otherPrimaryCartItemProperties.Name;
    }

    const hasFullNameField = otherPrimaryCartItemProperties['Full Name'];
    if (hasFullNameField) {
      fieldNames['Full Name'] = otherPrimaryCartItemProperties['Full Name'];
      delete otherPrimaryCartItemProperties['Full Name'];
    }

    if (!hideFontsAndFields) {
      Object.entries(otherPrimaryCartItemProperties).forEach(([key, value]) => {
        if (!key.startsWith('_')) {
          fieldNames[key] = value;
          delete otherPrimaryCartItemProperties[key];
        }
      });
    }

    const userProperties: { [key: string]: string | undefined } = {
      Style,
      Color: Palette
    };

    if (fonts?.length > 1) {
      userProperties.Font = Font;
    }

    if (showIcons) {
      userProperties.Icon = Icon;
    }

    const shapes = [];
    if (
      showLabelShapes &&
      properties.labelShape &&
      properties.labelShape?.display_name
    ) {
      shapes.push(properties.labelShape?.display_name);
    }
    apiProduct?.label_slots?.forEach(labelSlot => {
      const labelId = slots?.[labelSlot.product_label_slot_id]?.labelId;
      const label = apiProduct?.labels.find(l => l.label_id === labelId);
      const isIterative = label?.iterative_style_ids?.includes(
        properties?.style?.style_id ?? ''
      );
      const hasMultipleVariants = labelHasMultipleVariants[labelId || ''];

      if (hasMultipleVariants && !isIterative) {
        const variantId =
          properties.slots[labelSlot.product_label_slot_id].variantId;
        const variant = label?.variants.find(
          v => v.label_variant_id === variantId
        );
        if (variant?.system_name) shapes.push(variant.system_name);
      }
    });

    if (shapes.length) {
      userProperties.Shape = shapes.join(', ');
    }

    if (materials?.length > 1) {
      userProperties['Clothing Type'] = clothingType;
    }

    const quantity = getQuantityProperty(properties.slots, apiProduct);

    // Build properties object - include metadata fields with underscore prefix
    const itemProperties: Record<string, unknown> = {
      Labels: quantity.toString(),
      ...fieldNames,
      ...userProperties,
      ...otherPrimaryCartItemProperties,
      _order_system: 'NEW',
      _cart_item_id: primaryCartItemId,
      _product_id: apiProduct?.product_id,
      _style_id: properties.style?.style_id,
      _palette_id: properties.palette?.palette_id,
      _font_id: properties.font?.font_id,
      _icon_id: properties.icon?.icon_id,
      _material_id: properties.clothingType?.material_id,
      '_partner-origin': `${
        window.location != window.parent.location
          ? document.referrer
          : document.location.href
      }`
    };

    if (partnerKey) {
      itemProperties._partner_key = partnerKey;
    }

    // If an embed add-to-cart handler is provided, call it instead of redirecting
    if (onEmbedAddToCart) {
      const properties: Record<string, string> = {};
      for (const [k, v] of Object.entries(itemProperties)) {
        if (v != null) properties[k] = String(v);
      }
      onEmbedAddToCart({
        variantId: Number(shopifyVariantId),
        quantity: 1,
        properties
      });
      setButtonDisabled(false);
      setButtonText('Add to Cart');
      return;
    }

    // Step 3: Build cart permalink using your base64 encoding
    const cartInput = {
      items: [
        {
          id: shopifyVariantId || '',
          quantity: 1,
          properties: itemProperties
        }
      ]
    };

    const permalink = toShopifyCartPermalink(cartInput, shopifyDomain);

    // Step 4: Redirect to Shopify cart
    window.open(permalink, '_blank');

    // Reset state
    setButtonDisabled(false);
    setButtonText('Buy Now');
  }, [
    customButtonText,
    defaultButtonText,
    embedShopifyVariantId,
    properties,
    apiProduct,
    primaryCartItemProperties,
    hideFontsAndFields,
    fonts?.length,
    showIcons,
    showLabelShapes,
    materials?.length,
    slots,
    labelHasMultipleVariants,
    onEmbedAddToCart
  ]);

  const handleClick = useCallback(async () => {
    setButtonError(null);
    onClick?.();
    if (!validateMultiConfigNames()) return;
    if (isReadyForCart) {
      if (isEmbedMode) {
        setButtonText('Redirecting...');
        setButtonDisabled(true);
        redirectToShopifyCheckout();
      } else {
        setButtonText(updatingCartItem ? 'Saving' : 'Adding to Cart');
        setButtonDisabled(true);
        sendItemToCart();
      }
    }
  }, [
    updatingCartItem,
    isReadyForCart,
    onClick,
    sendItemToCart,
    isEmbedMode,
    redirectToShopifyCheckout,
    validateMultiConfigNames
  ]);

  return (
    <>
      <ContainedButton
        style={style}
        size="large"
        className="mt-5 flex-1"
        disabled={buttonDisabled || belowConfigMinimum}
        onClick={handleClick}
      >
        {buttonText}
      </ContainedButton>
      {buttonError && (
        <p className="mt-1 text-center text-xs text-red-500">{buttonError}</p>
      )}
    </>
  );
}
